'use client';
import React, { useState, useEffect } from 'react';
import { Clock, Layers, Info, ChevronDown, ChevronUp, Loader2, AlertCircle } from 'lucide-react';
import EmagramChart from './EmagramChart';
import WindBarbTable from './WindBarbTable';
import { DailySoaringForecast, generateSounding } from '@/lib/mockWeatherData';
import { fetchSoaringForecast, fetchHourlySoundings } from '@/lib/openMeteoApi';
import { useAerodrome } from '@/context/AerodromeContext';
import type { SoundingLevel } from '@/lib/mockWeatherData';
import { analyzeEmagramHour } from '@/lib/emagramAnalysis';

const LAYER_EXPLANATIONS = [
  {
    id: 'expl-bubble',
    color: '#cc2200',
    label: '🔴 Bulle d\'air (déclenchement)',
    text: 'La bulle d\'air est une masse d\'air chaud qui se détache du sol quand la température atteint le seuil de déclenchement. Sur l\'émagramme, c\'est le point rouge en bas à droite. L\'air monte en suivant l\'adiabate sèche (ligne rouge diagonale).',
  },
  {
    id: 'expl-dalr',
    color: '#cc3300',
    label: '↗ Adiabates sèches (lignes rouges)',
    text: 'Ces lignes diagonales représentent le refroidissement d\'une bulle d\'air sec en montant : 1°C tous les 100m. Si la courbe de température atmosphérique est à gauche de l\'adiabate, l\'air est instable → thermiques forts !',
  },
  {
    id: 'expl-sat',
    color: '#60a5fa',
    label: '↗ Adiabates saturées (tirets bleus)',
    text: 'Une fois que la bulle d\'air atteint son point de rosée (base des cumulus), elle se refroidit moins vite car la condensation libère de la chaleur. Le taux de refroidissement passe à ~6°C/km au lieu de 10°C/km.',
  },
  {
    id: 'expl-lcl',
    color: '#00d4ff',
    label: '☁ Base des cumulus (Plafond)',
    text: 'Le niveau de condensation par soulèvement (LCL). C\'est là où se forme la base des nuages. Plus l\'écart T/Td est faible en surface, plus la base est basse. Calculé par : altitude ≈ (T - Td) × 125 mètres.',
  },
  {
    id: 'expl-inversion',
    color: '#fbbf24',
    label: '⚠ Inversion de température',
    text: 'Zone où la température augmente avec l\'altitude (zone jaune). Elle bloque les thermiques comme un couvercle. Les bulles d\'air ne peuvent pas la traverser, ce qui limite le plafond de vol.',
  },
  {
    id: 'expl-thermal-top',
    color: '#a78bfa',
    label: '↑ Plafond thermique',
    text: 'Altitude où la bulle d\'air ascendante a la même température que l\'air environnant. Elle s\'arrête de monter. C\'est le plafond de vol en thermique. Plus il est haut, plus les vols sont longs !',
  },
];

export default function EmagramContent() {
  const { selectedAerodrome, hydrated } = useAerodrome();
  const [selectedHour, setSelectedHour] = useState(12);
  const [showDalr, setShowDalr] = useState(true);
  const [showDewpoint, setShowDewpoint] = useState(true);
  const [showThermalTop, setShowThermalTop] = useState(true);
  const [expandedExpl, setExpandedExpl] = useState<string | null>('expl-bubble');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [forecast, setForecast] = useState<DailySoaringForecast | null>(null);
  const [hourlySoundings, setHourlySoundings] = useState<Record<number, SoundingLevel[]>>({});
  const [flyingHours, setFlyingHours] = useState<number[]>(Array.from({ length: 16 }, (_, i) => i + 6));

  useEffect(() => {
    async function load() {
      if (!hydrated) return;
      setForecast(null);
      setHourlySoundings({});
      setLoading(true);
      setError(null);
      setWarning(null);
      try {
        const today = new Date().toISOString().split('T')[0];
        const soaringResult = await fetchSoaringForecast(selectedAerodrome);
        const todayForecast = soaringResult.today;
        setForecast(todayForecast);

        const sunriseH = parseInt(todayForecast.sunrise.split(':')[0], 10) || 6;
        const sunsetH = parseInt(todayForecast.sunset.split(':')[0], 10) || 21;
        const hours = Array.from({ length: sunsetH - sunriseH + 1 }, (_, i) => i + sunriseH);
        setFlyingHours(hours);
        setSelectedHour(Math.min(12, sunsetH));

        try {
          const soundings = await fetchHourlySoundings(selectedAerodrome, today);
          setHourlySoundings(soundings);
        } catch (soundErr) {
          console.warn('Hourly sounding load warning:', soundErr);
          setHourlySoundings(Object.fromEntries(hours.map((h) => [h, generateSounding(h)])));
          setWarning('Sondage Open-Meteo indisponible temporairement : profil de secours affiche pour garder l emagramme lisible.');
        }
      } catch (err) {
        console.warn('Emagram load warning:', err);
        setError('Impossible de charger les donnees Open-Meteo pour l emagramme.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [hydrated, selectedAerodrome.lat, selectedAerodrome.lon]);

  const currentSounding = hourlySoundings[selectedHour] ?? null;
  const convStartH = forecast ? parseInt(forecast.convectionStart?.split(':')[0] || '10', 10) : 10;
  const convEndH = forecast ? parseInt(forecast.convectionEnd?.split(':')[0] || '18', 10) : 18;

  return (
    <div className="min-h-screen px-3 sm:px-4 md:px-6 xl:px-8 2xl:px-10 py-4 sm:py-6 max-w-[1900px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Émagramme Planeur</h1>
          <p className="text-xs sm:text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Sondage à {selectedAerodrome.icao} — {selectedAerodrome.name} · {selectedHour?.toString()?.padStart(2, '0')}:00
          </p>
        </div>
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs whitespace-nowrap self-start sm:self-auto"
          style={{ backgroundColor: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.15)' }}
        >
          <Info size={13} style={{ color: '#00d4ff' }} />
          <span style={{ color: 'var(--text-muted)' }}>
            Changez l&apos;heure pour voir l&apos;évolution
          </span>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center gap-4 py-24">
          <Loader2 size={32} className="animate-spin" style={{ color: '#00d4ff' }} />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Chargement du sondage pour {selectedAerodrome.icao} — {selectedAerodrome.name}…
          </p>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center gap-4 py-16 rounded-2xl"
          style={{ backgroundColor: 'var(--surface)', border: '1px solid rgba(248,113,113,0.2)' }}>
          <AlertCircle size={40} style={{ color: '#f87171' }} />
          <p className="text-sm" style={{ color: '#f87171' }}>{error}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_352px] gap-4">
          {/* Main emagram */}
          <div
            className="rounded-xl p-4 sm:p-5"
            style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}
          >
            {/* Time slider */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Clock size={15} style={{ color: '#00d4ff' }} />
                  <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Sondage à {selectedHour?.toString()?.padStart(2, '0')}:00
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>
                    ☀ {forecast?.sunrise}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>→</span>
                  <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>
                    🌆 {forecast?.sunset}
                  </span>
                </div>
              </div>

              {/* Hour selector — touch-friendly larger buttons on mobile */}
              <div className="flex gap-1 flex-wrap">
                {flyingHours?.map((h) => {
                  const isConv = h >= convStartH && h <= convEndH;
                  const isSelected = h === selectedHour;
                  return (
                    <button
                      key={`hour-btn-${h}`}
                      onClick={() => setSelectedHour(h)}
                      className="flex-1 min-w-[2.5rem] py-2 sm:py-1.5 rounded-lg text-xs font-mono font-medium transition-all duration-150 active:scale-95 touch-manipulation"
                      style={{
                        backgroundColor: isSelected
                          ? '#00d4ff'
                          : isConv
                            ? 'rgba(52,211,153,0.12)'
                            : 'var(--surface-elevated)',
                        color: isSelected
                          ? '#0a0f1e'
                          : isConv
                            ? '#34d399' : 'var(--text-muted)',
                        border: `1px solid ${isSelected ? '#00d4ff' : isConv ? 'rgba(52,211,153,0.25)' : 'var(--border)'}`,
                      }}
                    >
                      {h?.toString()?.padStart(2, '0')}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-3 mt-2 text-xs">
                <span className="w-3 h-3 rounded" style={{ backgroundColor: 'rgba(52,211,153,0.2)', display: 'inline-block' }} />
                <span style={{ color: 'var(--text-muted)' }}>Zone convective ({forecast?.convectionStart} → {forecast?.convectionEnd})</span>
              </div>
            </div>

            {/* Layer toggles — scrollable on mobile */}
            <div className="flex gap-2 mb-4 overflow-x-auto pb-1 -mx-1 px-1">
              {[
                { key: 'dalr', label: 'Gradient adiabatique (DALR)', active: showDalr, toggle: () => setShowDalr(!showDalr), color: '#f59e0b' },
                { key: 'dewpoint', label: 'Point de rosée', active: showDewpoint, toggle: () => setShowDewpoint(!showDewpoint), color: '#3b82f6' },
                { key: 'thermal', label: 'Plafond thermique', active: showThermalTop, toggle: () => setShowThermalTop(!showThermalTop), color: '#a78bfa' },
              ]?.map((tog) => (
                <button
                  key={`tog-${tog?.key}`}
                  onClick={tog?.toggle}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-150 active:scale-95 touch-manipulation whitespace-nowrap flex-shrink-0"
                  style={{
                    backgroundColor: tog?.active ? `${tog?.color}18` : 'var(--surface-elevated)',
                    border: `1px solid ${tog?.active ? `${tog?.color}40` : 'var(--border)'}`,
                    color: tog?.active ? tog?.color : 'var(--text-muted)',
                  }}
                >
                  <Layers size={12} />
                  {tog?.label}
                </button>
              ))}
            </div>

            <EmagramChart
              key={`chart-${selectedAerodrome.icao}-${selectedAerodrome.lat}-${selectedAerodrome.lon}`}
              hour={selectedHour}
              showDalr={showDalr}
              showDewpoint={showDewpoint}
              showThermalTop={showThermalTop}
              soundingData={currentSounding}
            />
          </div>

          {/* Right panel: wind barbs + explanations — stacked below on mobile */}
          <div className="flex flex-col gap-4">
            {/* Wind barbs */}
            <div
              className="rounded-xl p-4"
              style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}
            >
              <WindBarbTable hour={selectedHour} soundingData={currentSounding} />
            </div>

            {/* Explanations for beginners */}
            <div
              className="rounded-xl p-4"
              style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}
            >
              <div className="flex items-center gap-2 mb-3">
                <Info size={14} style={{ color: '#00d4ff' }} />
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Lire l&apos;émagramme</h3>
              </div>
              <div className="space-y-2">
                {LAYER_EXPLANATIONS?.map((expl) => (
                  <div
                    key={expl?.id}
                    className="rounded-lg overflow-hidden"
                    style={{ border: '1px solid var(--border)' }}
                  >
                    <button
                      onClick={() => setExpandedExpl(expandedExpl === expl?.id ? null : expl?.id)}
                      className="w-full flex items-center justify-between px-3 py-3 text-left hover:bg-white/5 transition-colors touch-manipulation"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: expl?.color }}
                        />
                        <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{expl?.label}</span>
                      </div>
                      {expandedExpl === expl?.id
                        ? <ChevronUp size={12} style={{ color: 'var(--text-muted)' }} />
                        : <ChevronDown size={12} style={{ color: 'var(--text-muted)' }} />
                      }
                    </button>
                    {expandedExpl === expl?.id && (
                      <div className="px-3 pb-3">
                        <p className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>{expl?.text}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Key values for selected hour */}
            <div
              className="rounded-xl p-4"
              style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}
            >
              <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                Valeurs clés à {selectedHour?.toString()?.padStart(2, '0')}h
              </h3>
              {(() => {
                const h = forecast?.hourly?.find(hh => hh?.time === selectedHour) || forecast?.hourly?.[0];
                const sounding = currentSounding;
                const surfaceLevel = sounding?.[0];
                const analysis = sounding && sounding.length > 0 ? analyzeEmagramHour(sounding) : null;
                const statusColor =
                  analysis?.status === 'usable-cumulus' || analysis?.status === 'usable-blue'
                    ? '#34d399'
                    : analysis?.status === 'weak'
                      ? '#fbbf24'
                      : analysis?.status === 'overdeveloping'
                        ? '#fb7185'
                        : 'var(--text-muted)';
                const layerWind = analysis?.windLayerKmh ?? h?.windLayer ?? null;
                const shear = analysis?.windShearKmh ?? h?.windShear ?? null;
                const lclNotReached = !!analysis && analysis.lclAGL > analysis.thermalTopAGL;
                return (
                  <div className="space-y-2">
                    {analysis?.summary && (
                      <p className="text-xs leading-relaxed mb-3" style={{ color: statusColor }}>
                        {analysis.summary}
                      </p>
                    )}
                    {lclNotReached && (
                      <p className="text-xs leading-relaxed mb-3" style={{ color: '#fbbf24' }}>
                        LCL non atteint : la base Cu theorique est plus haute que le sommet thermique calcule.
                      </p>
                    )}
                    {[
                      { label: 'Terrain', value: `${selectedAerodrome.icao} · ${selectedAerodrome.elevation}m`, color: '#00d4ff' },
                      { label: 'Temp. sol', value: h ? `${h.temp}°C` : (surfaceLevel ? `${surfaceLevel.temp}°C` : '--'), color: '#f87171' },
                      { label: 'Point de rosée', value: h ? `${h.dewpoint}°C` : (surfaceLevel ? `${surfaceLevel.dewpoint}°C` : '--'), color: '#60a5fa' },
                      { label: 'Lecture', value: analysis?.label ?? h?.emagramStatus ?? '--', color: statusColor },
                      { label: 'Base Cu theorique', value: analysis?.lclAGL ? `${analysis.lclAGL}m` : (h?.lcl ? `${h.lcl}m` : '--'), color: '#00d4ff' },
                      { label: 'Sommet thermique', value: analysis?.thermalTopAGL ? `${analysis.thermalTopAGL}m` : (h?.thermalTop ? `${h.thermalTop}m` : '--'), color: '#a78bfa' },
                      { label: 'Plafond exploitable', value: h?.cumulusBase && h.cumulusBase > 0 ? `${h.cumulusBase}m` : (analysis?.usableCeilingAGL ? `${analysis.usableCeilingAGL}m` : '--'), color: '#34d399' },
                      { label: 'VZ profil', value: h?.vz && h.vz > 0 ? `${h.vz} m/s` : (analysis?.maxVz ? `${analysis.maxVz} m/s` : '--'), color: '#fbbf24' },
                      { label: 'Vent couche', value: layerWind !== null ? `${layerWind} km/h` : '--', color: '#f59e0b' },
                      { label: 'Cisaillement', value: shear !== null ? `${shear} km/h` : '--', color: shear && shear > 18 ? '#fb7185' : 'var(--text-muted)' },
                      { label: 'Vent sol', value: surfaceLevel ? `${surfaceLevel.windSpeed} km/h ${surfaceLevel.windDir}°` : (h ? `${h.windSpeed} km/h ${h.windDir}°` : '--'), color: '#fbbf24' },
                      { label: 'Nébulosité', value: h ? `${h.cloudCover} %` : '--', color: 'var(--text-muted)' },
                    ]?.map((kv) => (
                      <div key={`kv-${kv?.label}`} className="flex items-center justify-between">
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{kv?.label}</span>
                        <span className="font-mono text-sm font-semibold" style={{ color: kv?.color }}>
                          {kv?.value}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
